import {
  Dropdown,
  Icon,
  InputMasked,
  InputText,
  InputTextarea,
  RadioButton,
} from "@uigovpe/components";
import { Controller, useFormContext } from "react-hook-form";
import type { CategoryOption, ProfileFormData } from "../../../types";
import { isValidCnpj } from "@/utils/cnpj";
import { normalizeCnpj } from "@/utils/formats";
import { isValidWebsite } from "@/utils/validations";
import VisualIdentityImageUpload from "./visual-identity-image-upload";

type Props = { categories: CategoryOption[]; isLoadingCategories: boolean };

export default function ProfileStepTwo({
  categories,
  isLoadingCategories,
}: Props) {
  const {
    control,
    register,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<ProfileFormData>();
  const participates = watch("participatesInAssociation");
  const hasBusinessImage = Boolean(
    watch("coverImage")?.[0] ||
      watch("existingCoverImage") ||
      watch("logoImage")?.[0] ||
      watch("existingLogoImage"),
  );

  const updateAssociation = (value: boolean) => {
    setValue("participatesInAssociation", value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (!value) {
      setValue("associationName", "", { shouldDirty: true });
      clearErrors("associationName");
    }
  };

  return (
    <div className="flex! w-full! flex-col! gap-8!">
      <section className="flex! flex-col! gap-5!">
        <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
          <Icon icon="person" className="text-primary!" />
          <h3 className="m-0! text-base! font-bold!">Sobre seu negócio</h3>
        </div>
        <div className="grid! grid-cols-1! gap-x-6! gap-y-5! md:grid-cols-6!">
          <div className="md:col-span-2!">
            <InputText
              label="Nome do negócio"
              placeholder="Ex: Amélia Artes e Sabores"
              supportText={
                errors.businessName?.message ?? "Nome exibido na sua vitrine."
              }
              invalid={Boolean(errors.businessName)}
              className="w-full!"
              {...register("businessName", {
                required: "Informe o nome do negócio",
                minLength: { value: 2, message: "Use pelo menos 2 caracteres" },
              })}
            />
          </div>
          <div className="md:col-span-2!">
            <Controller
              name="cnpj"
              control={control}
              rules={{
                validate: (value) =>
                  !value ||
                  (normalizeCnpj(value).length === 14 && isValidCnpj(value)) ||
                  "Informe um CNPJ válido",
              }}
              render={({ field, fieldState }) => (
                <InputMasked
                  label="CNPJ (opcional)"
                mask="**.***.***/****-99"
                placeholder="00.000.000/0000-00"
                  value={field.value}
                  supportText={
                    fieldState.error?.message ??
                    "Preencha se o seu negócio tiver CNPJ."
                  }
                  invalid={Boolean(fieldState.error)}
                  className="w-full!"
                  onBlur={field.onBlur}
                  onChange={(event) =>
                    field.onChange(normalizeCnpj(event.target.value).slice(0, 14))
                  }
                />
              )}
            />
          </div>
          <div className="md:col-span-2!">
            <Controller
              name="website"
              control={control}
              rules={{
                validate: (value) =>
                  isValidWebsite(value) || "Informe um website válido",
              }}
              render={({ field, fieldState }) => (
                <InputText
                  label="Website (opcional)"
                  placeholder="seudominio.com.br"
                  value={field.value}
                  supportText={
                    fieldState.error?.message ??
                    "Página oficial do seu negócio."
                  }
                  invalid={Boolean(fieldState.error)}
                  className="w-full!"
                  onBlur={field.onBlur}
                  onChange={(event) =>
                    field.onChange(event.target.value.replace(/\s/g, ""))
                  }
                />
              )}
            />
          </div>
          <div className="md:col-span-3!">
            <Controller
              name="businessCategory"
              control={control}
              rules={{ required: "Selecione uma categoria" }}
              render={({ field, fieldState }) => (
                <Dropdown
                  label="Categoria"
                  value={field.value}
                  options={categories}
                  placeholder={
                    isLoadingCategories
                      ? "Carregando categorias..."
                      : "Selecione uma categoria"
                  }
                  supportText={
                    fieldState.error?.message ??
                    "Escolha a categoria que melhor representa seu negócio."
                  }
                  invalid={Boolean(fieldState.error)}
                  disabled={isLoadingCategories}
                  className="w-full!"
                  onChange={(event) => field.onChange(event.value)}
                />
              )}
            />
          </div>
          <fieldset className="md:col-span-3!">
            <legend className="mb-5! text-sm! font-medium!">
              Participa de Associação/Instituto/Cooperativa?
            </legend>
            <div className="flex! gap-8!">
              <RadioButton
                label="Sim"
                name="association"
                value="yes"
                checked={participates}
                onChange={() => updateAssociation(true)}
              />
              <RadioButton
                label="Não"
                name="association"
                value="no"
                checked={!participates}
                onChange={() => updateAssociation(false)}
              />
            </div>
          </fieldset>
          {participates && (
            <div className="md:col-span-3!">
              <InputText
                label="Nome da Associação/Instituto/Cooperativa"
                placeholder="Ex: Mulheres Empreendedoras PE"
                supportText={errors.associationName?.message}
                invalid={Boolean(errors.associationName)}
                className="w-full!"
                {...register("associationName", {
                  required: "Informe o nome da associação",
                })}
              />
            </div>
          )}
        </div>
      </section>

      <section className="flex! flex-col! gap-5!">
        <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
          <Icon icon="share" outline className="text-primary!" />
          <h3 className="m-0! text-base! font-bold!">Rede social</h3>
        </div>
        <div className="grid! grid-cols-1! gap-6! md:grid-cols-3!">
          <Controller
            name="instagram"
            control={control}
            render={({ field }) => (
              <InputText
                label="Instagram"
                placeholder="@seuusuario"
                supportText="Perfil usado para divulgar seu trabalho."
                className="w-full!"
                value={field.value}
                onBlur={field.onBlur}
                onChange={(event) => {
                  const username = event.target.value
                    .replace(/\s/g, "")
                    .replace(/^@+/, "");
                  field.onChange(username ? `@${username}` : "");
                }}
              />
            )}
          />
          <Controller
            name="facebook"
            control={control}
            render={({ field }) => (
              <InputText
                label="Facebook"
                placeholder="facebook.com/seunegocio"
                supportText="Página ou perfil do seu negócio."
                className="w-full!"
                value={field.value}
                onBlur={field.onBlur}
                onChange={(event) => {
                  const profile = event.target.value
                    .replace(/\s/g, "")
                    .replace(/^https?:\/\//i, "")
                    .replace(/^www\./i, "")
                    .replace(/^facebook\.com\/?/i, "")
                    .replace(/^\/+/, "");
                  field.onChange(profile ? `facebook.com/${profile}` : "");
                }}
              />
            )}
          />
          <Controller
            name="tiktok"
            control={control}
            render={({ field }) => (
              <InputText
                label="TikTok"
                placeholder="@seuusuario"
                supportText="Canal usado para divulgar seus produtos."
                className="w-full!"
                value={field.value}
                onBlur={field.onBlur}
                onChange={(event) => {
                  const username = event.target.value
                    .replace(/\s/g, "")
                    .replace(/^@+/, "");
                  field.onChange(username ? `@${username}` : "");
                }}
              />
            )}
          />
        </div>
        <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
          <Icon icon="photo" outline className="text-primary!" />
          <h3 className="m-0! text-base! font-bold!">
            Identidade visual do negócio
          </h3>
        </div>
        <div className="grid! grid-cols-1! gap-6! md:grid-cols-2!">
          <VisualIdentityImageUpload type="cover" />
          <VisualIdentityImageUpload type="logo" />
        </div>
        {hasBusinessImage && (
          <div className="flex! gap-3! rounded-md! border! border-violet-light! bg-violet-ultralight/50! p-4! text-sm! text-primary!">
            <Icon icon="info" className="mt-0.5! shrink-0! text-[20px]! text-primary!" />
            <div>
              <p className="m-0! font-semibold! text-primary!">Importante</p>
              <p className="mt-1! mb-0! text-zinc-700!">
                A descrição da imagem ajuda pessoas que usam leitores de tela a
                entender o conteúdo das imagens. Revise e ajuste se necessário
                para que a descrição fique clara e completa.
              </p>
            </div>
          </div>
        )}
        <Controller
          name="businessDescription"
          control={control}
          rules={{
            required: "Descreva seu negócio",
            minLength: { value: 20, message: "Use pelo menos 20 caracteres" },
          }}
          render={({ field, fieldState }) => (
            <div>
              <InputTextarea
                label="Descrição do negócio"
                placeholder="Conte sobre seu negócio, seus produtos e diferenciais"
                rows={5}
                maxLength={500}
                value={field.value}
                supportText={
                  fieldState.error?.message ??
                  "Esse texto ajudará visitantes a conhecerem seu trabalho."
                }
                invalid={Boolean(fieldState.error)}
                className="w-full!"
                onBlur={field.onBlur}
                onChange={field.onChange}
              />
              <div className="mt-1! text-right! text-xs! text-zinc-600!">
                {field.value.length}/500 caracteres
              </div>
            </div>
          )}
        />
      </section>
    </div>
  );
}
